import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Heart, Trash2, ShoppingCart } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { Seo } from '../../../../components/seo.js';
import {
  StorefrontEmptyState,
  StorefrontErrorState,
  StorefrontLoadingState,
} from '../../components/states.js';
import { useWishlist, useWishlistMutations } from '../hooks/use-wishlist.js';
import { removeWishlistItem } from '../api/wishlist-api.js';
import type { WishlistItem } from '../types/wishlist.js';
import { StorefrontApiError } from '../../../../lib/api/storefront-client.js';

/** Same query key `useWishlist`/`useWishlistMutations` share -- used only for the optimistic-remove rollback below, never as a second source of truth. */
const WISHLIST_KEY = ['storefront', 'wishlist'] as const;

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof StorefrontApiError) return error.message;
  return fallback;
}

interface WishlistRowProps {
  item: WishlistItem;
  onRemove: (item: WishlistItem) => void;
  onMoveToCart: (item: WishlistItem) => void;
  isRemoving: boolean;
  isMoving: boolean;
}

function WishlistRow({ item, onRemove, onMoveToCart, isRemoving, isMoving }: WishlistRowProps) {
  const isUnavailable = item.availability === 'unavailable';

  return (
    <li className="flex flex-col gap-3 border-b border-gray-100 py-4 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        {isUnavailable ? (
          <p className="text-sm font-medium text-gray-500">This item is no longer available.</p>
        ) : (
          <Link
            to={`${ROUTE_NAMES.product}/${item.productSlug}`}
            className="focus-visible:ring-brand truncate text-sm font-semibold text-gray-900 hover:underline focus-visible:outline-none focus-visible:ring-2"
          >
            {item.productName}
          </Link>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!isUnavailable && (
          <button
            type="button"
            onClick={() => onMoveToCart(item)}
            disabled={isMoving || isRemoving}
            className="focus-visible:ring-brand inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ShoppingCart className="h-3.5 w-3.5" aria-hidden="true" />
            {isMoving ? 'Moving…' : 'Move to cart'}
          </button>
        )}
        <button
          type="button"
          onClick={() => onRemove(item)}
          disabled={isRemoving || isMoving}
          className="focus-visible:ring-brand inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          Remove
        </button>
      </div>
    </li>
  );
}

async function requireToken(getToken: () => Promise<string | null>): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new StorefrontApiError('AUTHENTICATION_ERROR', 'Your session has expired. Please sign in again.', 401);
  }
  return token;
}

export function WishlistPage() {
  const { data: items, isLoading, isError, refetch } = useWishlist();
  const { moveToCart } = useWishlistMutations();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [announcement, setAnnouncement] = useState('');
  const [banner, setBanner] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
  const [pendingMoveId, setPendingMoveId] = useState<string | null>(null);

  /**
   * A local mutation (rather than the shared hook's `remove`) so the
   * optimistic list-membership removal + rollback-on-error can live in
   * `onMutate`/`onError` -- TanStack Query v5 only accepts `onMutate` in
   * the `useMutation` config itself, not in per-call `mutate(vars, opts)`
   * options, so this can't be layered on top of `useWishlistMutations()`.
   */
  const removeOptimistic = useMutation({
    mutationFn: async (itemId: string) => removeWishlistItem(itemId, { token: await requireToken(getToken) }),
    onMutate: async (itemId: string) => {
      await queryClient.cancelQueries({ queryKey: WISHLIST_KEY });
      const previous = queryClient.getQueryData<WishlistItem[]>(WISHLIST_KEY);
      queryClient.setQueryData<WishlistItem[]>(WISHLIST_KEY, (current) =>
        (current ?? []).filter((existing) => existing.id !== itemId),
      );
      return { previous };
    },
    onError: (error, _itemId, context) => {
      if (context?.previous) queryClient.setQueryData(WISHLIST_KEY, context.previous);
      setBanner({ type: 'error', message: errorMessage(error, 'Could not remove this item. Please try again.') });
    },
    onSuccess: (updatedItems) => queryClient.setQueryData(WISHLIST_KEY, updatedItems),
  });

  const handleRemove = (item: WishlistItem) => {
    setPendingRemoveId(item.id);
    setBanner(null);
    removeOptimistic.mutate(item.id, {
      onSuccess: () => setAnnouncement(`${item.productName ?? 'Item'} removed from wishlist`),
      onSettled: () => setPendingRemoveId(null),
    });
  };

  const handleMoveToCart = (item: WishlistItem) => {
    setPendingMoveId(item.id);
    setBanner(null);
    moveToCart.mutate(
      { itemId: item.id, input: {} },
      {
        onSuccess: () => {
          setBanner({ type: 'success', message: `${item.productName ?? 'Item'} moved to cart.` });
          setAnnouncement(`${item.productName ?? 'Item'} moved to cart`);
        },
        onError: (error) => {
          setBanner({ type: 'error', message: errorMessage(error, 'Could not move this item to your cart. Please try again.') });
        },
        onSettled: () => setPendingMoveId(null),
      },
    );
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <Seo title="My Wishlist" noindex />
      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>

      <div className="mb-6 flex items-center gap-2">
        <Heart className="h-6 w-6 text-gray-400" aria-hidden="true" />
        <h1 className="text-xl font-bold text-gray-900">My Wishlist</h1>
      </div>

      {banner && (
        <div
          role="status"
          className={`mb-4 rounded-xl border px-4 py-3 text-sm font-medium ${
            banner.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {banner.message}
        </div>
      )}

      {isLoading && <StorefrontLoadingState label="Loading your wishlist…" />}

      {isError && !isLoading && (
        <StorefrontErrorState message="Could not load your wishlist." onRetry={() => void refetch()} />
      )}

      {!isLoading && !isError && items && items.length === 0 && (
        <StorefrontEmptyState
          title="Your wishlist is empty"
          description="Save products you love and find them here later."
          action={
            <Link
              to={ROUTE_NAMES.shop}
              className="focus-visible:ring-brand rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2"
            >
              Browse products
            </Link>
          }
        />
      )}

      {!isLoading && !isError && items && items.length > 0 && (
        <ul className="rounded-2xl border border-gray-100 bg-white px-4">
          {items.map((item) => (
            <WishlistRow
              key={item.id}
              item={item}
              onRemove={handleRemove}
              onMoveToCart={handleMoveToCart}
              isRemoving={pendingRemoveId === item.id}
              isMoving={pendingMoveId === item.id}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
